import type { TranslationDict, InterpolationVars } from './types';
import { interpolate } from './interpolate';
import { formatPlural } from './plurals';
import type { SupportedLocale } from './types';

/**
 * Resolve a translation key from a dictionary
 */
function resolveValue(source: TranslationDict, key: string): string | undefined {
  const parts = key.split('.');
  let cur: any = source;
  
  for (const part of parts) {
    if (!cur || typeof cur !== 'object' || !(part in cur)) {
      return undefined;
    }
    cur = cur[part];
  }
  
  return typeof cur === 'string' ? cur : undefined;
}

export function createTranslator(
  dict: TranslationDict,
  locale: SupportedLocale = 'en',
  fallbackDict?: TranslationDict // Pass English dict as fallback
) {
  return function t(key: string, vars?: InterpolationVars): string {
    // Try current locale first
    let value = resolveValue(dict, key);
    
    // Fallback to English if missing and fallback dict provided
    if (value === undefined && fallbackDict && locale !== 'en') {
      value = resolveValue(fallbackDict, key);
      if (value !== undefined && import.meta.env.DEV) {
        console.log(`[i18n] Using EN fallback for key: ${key} (missing in ${locale})`);
      }
    }
    
    // If still undefined, warn and return key
    if (value === undefined) {
      if (import.meta.env.DEV) {
        console.warn(`[i18n] Missing translation key: ${key} (no fallback available)`);
      }
      return key;
    }
    
    // Check for ICU MessageFormat patterns
    if (value.includes('plural') || value.includes('select')) {
      return formatPlural(value, vars || {}, locale);
    }
    
    // Otherwise use simple interpolation
    return interpolate(value, vars);
  };
}

